import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';

import { CustomETReport as ETReport, PDFImageProps, ETIReportItem, User } from '../../../utils/models';

import { getTutorials } from '../../../store/tutorials';
import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { getCurrentUser } from '../../../services/authService';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { reArrangePhotoRecord } from '../../../utils/utilities';

import Button from '../../themed/button';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomETReportForm from '../../forms/custom-mpi/customETReportForm';
import CustomCertificateForm, { ICertificate } from '../custom-general/custom-certificates/form';
import Conditional from '../../common/conditional';
import ETPDFReport from './';
import LoadingBadge from '../../themed/loading-badge';
import FVICoverData from '../../themed/fvi-cover-data';
import ETInspectionItems from './etInspectionItems';
import Drawing from '../../forms/custom-lpt/drawing';
import Elevation from '../../themed/instruction-modals/elevation';
import Modal from '../../themed/modal';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import VIPhotoRecordForm from '../../forms/visual-inspection/viPhotoRecordForm';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useProjectDetails from '../../../hooks/useProjectDetails';
import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CustomETReport: React.FC = () => {
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [etReportItems, setETReportItems] = useState<ETIReportItem[]>([]);
	const [etReport, setETReport] = useState(initialETData);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [photoRecord, setPhotoRecord] = useState<PDFImageProps[]>([]);
	const [drawing, setDrawing] = useState<PDFImageProps[]>([]);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [certificates, setCertificates] = useState<ICertificate>();

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { isSaving } = useAppSelector(getReportDetails);
	const { fviCoverData } = useAppSelector(getCoverData);

	const { role } = getCurrentUser() as User;

	const reportDetails = useMemo(() => {
		return {
			projectDetails,
			coverData,
			certificates,
			etReport,
			etReportItems,
			photoRecord,
			drawing,
			reportTitle,
		};
	}, [projectDetails, certificates, coverData, etReport, etReportItems, photoRecord, drawing, reportTitle]);

	const validationMessage = useMemo(() => {
		if (reportDetails.etReport.remarks.length === 0) return 'Enter a valid remark!';

		if (reportDetails.etReportItems.length === 0) return 'Upload at least one ET Report item!';

		if (reportDetails.photoRecord.length === 0) return 'Upload at least one photo!';

		if (reportDetails.drawing.length === 0) return 'General drawing not uploaded!';

		return ''
	}, [reportDetails]);

	const mappedPhotoNumbers = useMemo(() => {
		return photoRecord.map((photo) => ({ label: photo.caption, value: photo.refNo }));
	}, [photoRecord]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleSaveAndPreview = useCallback((): void => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

	const mapETReportItems = (photoRecord: PDFImageProps[], reportItems: ETIReportItem[]) => {
		const rearrangedImages = _.orderBy(reArrangePhotoRecord(photoRecord), ['refNo'], ['asc']);

		const updatedReportItems = reportItems.map((item) => ({ ...item, images: item.images.map((image) => {
			const match = _.find(rearrangedImages, ['caption', image.label]);
			if (match) return { label: match.caption, value: match.refNo };

			return image;
		})})) as ETIReportItem[];

		return { rearrangedImages, updatedReportItems };
	}

	useEffect(() => {
		const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
		if (cachedReport) {
			const { rearrangedImages, updatedReportItems } = mapETReportItems(cachedReport.photoRecord, cachedReport.etReportItems);

			if (cachedReport.certificates) {
				setCertificates(cachedReport.certificates);
			}

			setCoverData(cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData);
			setETReport(cachedReport.etReport);
			setETReportItems(updatedReportItems);
			setDrawing(cachedReport.drawing);
			setPhotoRecord(rearrangedImages);
			setReportTitle(cachedReport.reportTitle);
			dispatch(setLastPhotoNumber(rearrangedImages.length));
		} else if (fviCoverData.coverData.length > 0) {
			setCoverData(fviCoverData);
		}
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.ET));
	}, [dispatch]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<ETPDFReport {...reportDetails} />
			</Modal>

			<FVICoverData
				coverDataId={config.coverDataTemplates.exxonMobilET}
				isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilET && role.approveReports}
				subtitle='Update the default data for ET Cover Data. This is the default data used on all ET Reports'
			/>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title='Report Incomplete' body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>

			<CustomCertificateForm calibrationRequired certificates={certificates} onChangeCertificate={setCertificates} />

			<Zippy headerTitle="Drawing" isValid={drawing.length > 0}>
				<Drawing initialDrawing={drawing} onAdd={setDrawing} />
			</Zippy>

			<Zippy headerTitle="Eddy Current Test Report" isValid={etReport.remarks.length > 0}>
				<CustomETReportForm initialData={etReport} onProceed={setETReport} />
			</Zippy>

			<Zippy headerTitle="Eddy Current Inspection Report Data (ET)" isValid={etReportItems.length > 0}>
				<ETInspectionItems items={etReportItems} onChangeItems={setETReportItems} photoNumbers={mappedPhotoNumbers} />
			</Zippy>

			<Zippy headerTitle="General Photos" isValid={photoRecord.length > 0}>
				<VIPhotoRecordForm initialGeneralPhotos={photoRecord} onProceed={setPhotoRecord} />
			</Zippy>
			
			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving..." />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialETData: ETReport = {
	ndeTechnique: 'Scanning (Zig-Zag)',
	procedureNo: '',
	equipment: 'Olympus  Nortec 500',
	probe: 'OLYMPUS WLD-8-55/7L',
	material: 'Carbon Steel',
	probeFrequency: '100',
	scanningLevel: 'Gain 71.0db Phase Angle 132 deg',
	remarks: '',
	serialNo: 'U10413',
	calibrationBlk: 'Notch block',
	sensitivity: '1mm slot to 80% FSH',
	calibrationDue: new Date(),
};

export default CustomETReport;
